import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import { studentsApi } from './api';
import reStudyApi, { ReStudyRecord } from '../re-study/reStudyApi';
import ImagePreview from '../../../components/common/ImagePreview';
import { useMasterData } from '../../../hooks/useMasterData';
import { sessionRecordApi, SessionRecord } from '../session-record/api';
import { STUDENT_FIELDS } from './constants';
import { formatDateDDMMYYYY } from '../../../utils/dateFormatter';

const DetailItem: React.FC<{ label: string; value: any; fullWidth?: boolean; grid?: number }> = ({ label, value, fullWidth, grid = 6 }) => (
  <Grid item xs={12} md={fullWidth ? 12 : grid}>
    <Typography color="textSecondary" variant="caption" display="block" gutterBottom>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={500}>
      {value || '-'}
    </Typography>
  </Grid>
);

const AddressBlock: React.FC<{ address: any; fields: any[] }> = ({ address, fields }) => {
    if (!address) return <Typography color="textSecondary">Not Provided</Typography>;
    return (
        <Grid container spacing={2}>
            {fields.map((field) => (
                <DetailItem 
                    key={field.name} 
                    label={field.label} 
                    value={address[field.name]} 
                    grid={field.grid === 12 ? 12 : 6} // Use grid from config or default to 6 for address block
                    fullWidth={field.grid === 12}
                />
            ))}
        </Grid>
    );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StudentProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<any>(null);
  const [academicHistory, setAcademicHistory] = useState<SessionRecord[]>([]);
  const [reStudyHistory, setReStudyHistory] = useState<ReStudyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const { masters, fetchMasters } = useMasterData();
  const masterDocuments = masters.documents;

  const fetchStudent = async (studentId: string) => {
    try {
      const data = await studentsApi.getById(studentId);
      setStudent(data);
    } catch (error) {
      navigate('/scholar-register/students');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasters();
    if (id) {
      fetchStudent(id);
      sessionRecordApi
        .historyByStudent(id)
        .then(setAcademicHistory)
        .catch(() => {});
      reStudyApi
        .getStudentHistory(id)
        .then(setReStudyHistory)
        .catch(() => {});
    }
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (!student) return <Typography>Student not found</Typography>;

  const getFullUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    if (url.startsWith('http')) return url;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;
  };

  const getAddressByType = (type: string) => {
      return student.addresses?.find((a: any) => a.type === type);
  };

  const presentAddress = getAddressByType('PRESENT');
  const permanentAddress = getAddressByType('PERMANENT');

  const renderField = (field: any, data: any) => {
      let value = data[field.name];
      
      if (field.isRelation && value) {
          value = value[field.relationKey || 'name'];
      }
      
      if (field.type === 'date' && value) {
          value = formatDateDDMMYYYY(value);
      }

      if (field.type === 'boolean') {
          value = value ? 'Yes' : 'No';
      }

      return (
          <DetailItem 
            key={field.name} 
            label={field.label} 
            value={value} 
            grid={field.grid} 
          />
      );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      {/* Header Actions */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/scholar-register/students')}
            sx={{ mr: 2, textTransform: 'none' }}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h5" component="h1" fontWeight="500">
            Student Profile
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/scholar-register/session-record/${id}/edit`)}
          >
            Manage Sessions
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/scholar-register/students/edit/${id}`)}
          >
            Edit Student
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Top Summary Card */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 3 }}>
            <ImagePreview 
                src={student.studentPhotoUrl} 
                alt={`${student.firstName} ${student.lastName}`} 
                label="Student Photo"
                width={120}
                height={120}
                shape="rect"
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" gutterBottom>
                  {student.firstName} {student.middleName} {student.lastName}
              </Typography>
              <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography color="textSecondary" variant="subtitle2">Admission No</Typography>
                    <Typography variant="body1" fontWeight="500">{student.admissionNumber}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography color="textSecondary" variant="subtitle2">Scholar No</Typography>
                    <Typography variant="body1" fontWeight="500">{student.scholarNumber || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography color="textSecondary" variant="subtitle2">Class - Section</Typography>
                    <Typography variant="body1" fontWeight="500">{student.class?.name} - {student.section?.name}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography color="textSecondary" variant="subtitle2">Status</Typography>
                    <Chip 
                        label={student.status} 
                        color={student.status === 'ACTIVE' ? 'success' : 'default'} 
                        size="small" 
                        sx={{ fontWeight: 'bold' }}
                    />
                  </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Tabs & Content */}
        <Grid item xs={12}>
          <Paper sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                <Tab label="Personal Details" />
                <Tab label="Academic Details" />
                <Tab label="Parent Details" />
                <Tab label="Address Details" />
                <Tab label="Previous School" />
                <Tab label="Documents" />
                <Tab label="Academic History" />
                <Tab label="Status History" />
                <Tab label="Re-Study History" />
              </Tabs>
            </Box>

            {/* Tab 0: Personal Details */}
            <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3}>
                    {STUDENT_FIELDS.personal.map(field => renderField(field, student))}
                </Grid>
            </TabPanel>

            {/* Tab 1: Academic Details */}
            <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                    {STUDENT_FIELDS.academic.map(field => renderField(field, student))}
                </Grid>
            </TabPanel>

            {/* Tab 2: Parent Details */}
            <TabPanel value={tabValue} index={2}>
                <Box>
                    {/* Father */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1 }}>Father Details</Typography>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                             <ImagePreview src={student.parent?.fatherPhotoUrl || student.fatherPhotoUrl} alt="Father" label="Father Photo" />
                             <Grid container spacing={2} sx={{ flex: 1 }}>
                                {STUDENT_FIELDS.parent.father.map(field => renderField(field, student.parent || {}))}
                             </Grid>
                        </Box>
                    </Box>

                    {/* Mother */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1 }}>Mother Details</Typography>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                             <ImagePreview src={student.parent?.motherPhotoUrl || student.motherPhotoUrl} alt="Mother" label="Mother Photo" />
                             <Grid container spacing={2} sx={{ flex: 1 }}>
                                {STUDENT_FIELDS.parent.mother.map(field => renderField(field, student.parent || {}))}
                             </Grid>
                        </Box>
                    </Box>

                    {/* Guardian */}
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #eee', pb: 1 }}>Guardian Details</Typography>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                             <ImagePreview src={student.parent?.guardianPhotoUrl || student.guardianPhotoUrl} alt="Guardian" label="Guardian Photo" />
                             <Grid container spacing={2} sx={{ flex: 1 }}>
                                {STUDENT_FIELDS.parent.guardian.map(field => renderField(field, student.parent || {}))}
                             </Grid>
                        </Box>
                    </Box>
                </Box>
            </TabPanel>

            {/* Tab 3: Address Details */}
            <TabPanel value={tabValue} index={3}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom color="primary">Present Address</Typography>
                            <AddressBlock address={presentAddress} fields={STUDENT_FIELDS.address} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom color="primary">Permanent Address</Typography>
                            {student.isPermanentSameAsPresent ? (
                                <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, textAlign: 'center' }}>
                                    <Typography variant="subtitle1" fontWeight="bold">Same as Present Address</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        (Address details are identical to the present address)
                                    </Typography>
                                </Box>
                            ) : (
                                <AddressBlock address={permanentAddress} fields={STUDENT_FIELDS.address} />
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </TabPanel>

            {/* Tab 4: Previous School */}
            <TabPanel value={tabValue} index={4}>
                {student.previousSchool ? (
                  <Grid container spacing={3}>
                    <DetailItem label="Board" value={student.previousSchool?.board?.name || '-'} />
                    <DetailItem label="Passing Year" value={student.previousSchool?.passingYear || '-'} />
                    <DetailItem label="Medium" value={student.previousSchool?.medium?.name || '-'} />
                    <DetailItem label="Previous Class" value={student.previousSchool?.previousClass || '-'} />
                    <DetailItem label="TC Number" value={student.previousSchool?.tcNumber || '-'} />
                    <DetailItem label="TC Date" value={student.previousSchool?.tcDate ? formatDateDDMMYYYY(student.previousSchool.tcDate) : '-'} />
                    <DetailItem label="Leaving Reason" value={student.previousSchool?.leavingReason?.name || '-'} />
                    <DetailItem label="Result" value={student.previousSchool?.result || '-'} />
                  </Grid>
                ) : (
                  <Typography color="textSecondary">No previous school details available.</Typography>
                )}
            </TabPanel>

            {/* Tab 5: Documents */}
            <TabPanel value={tabValue} index={5}>
                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell>Document Name</TableCell>
                                <TableCell>Mandatory</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Uploaded At</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {masterDocuments
                                ?.filter((doc: any) => doc.applicableFor === 'STUDENT' || doc.applicableFor === 'BOTH' || !doc.applicableFor)
                                .map((masterDoc: any) => {
                                    const uploadedDoc = student.documents?.find((d: any) => d.documentId === masterDoc.id);
                                    const isMandatory = masterDoc.isMandatory;

                                    return (
                                        <TableRow key={masterDoc.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="500">{masterDoc.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {isMandatory ? <Chip label="Required" color="error" size="small" variant="outlined" /> : <Chip label="Optional" size="small" variant="outlined" />}
                                            </TableCell>
                                            <TableCell>
                                                {uploadedDoc ? (
                                                    <Chip icon={<CheckCircleIcon />} label="Uploaded" color="success" size="small" />
                                                ) : isMandatory ? (
                                                    <Chip icon={<CancelIcon />} label="Missing" color="error" size="small" />
                                                ) : (
                                                    <Chip label="Pending" size="small" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {uploadedDoc ? formatDateDDMMYYYY(uploadedDoc.uploadedAt) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {uploadedDoc ? (
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        {uploadedDoc.url.toLowerCase().match(/\.(jpeg|jpg|png|gif)$/) ? (
                                                            <ImagePreview 
                                                                src={uploadedDoc.url} 
                                                                alt={uploadedDoc.name} 
                                                                width={50} 
                                                                height={50}
                                                                label=""
                                                            />
                                                        ) : (
                                                            <Button 
                                                                variant="outlined" 
                                                                size="small" 
                                                                startIcon={<DownloadIcon />}
                                                                href={getFullUrl(uploadedDoc.url)}
                                                                target="_blank"
                                                            >
                                                                Download
                                                            </Button>
                                                        )}
                                                    </Box>
                                                ) : '-'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {(!masterDocuments || masterDocuments.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">No document requirements found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </TabPanel>

            {/* Tab 6: Academic History */}
            <TabPanel value={tabValue} index={6}>
                 {academicHistory && academicHistory.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Session</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>Section</TableCell>
                            <TableCell>Form No</TableCell>
                            <TableCell>Date of Admission</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                            {academicHistory.map((record) => (
                                <TableRow key={record.id}>
                                    <TableCell>{record.academicSession?.name}</TableCell>
                                    <TableCell>{record.class?.name}</TableCell>
                                    <TableCell>{record.section?.name}</TableCell>
                                    <TableCell>{record.formNumber || '-'}</TableCell>
                                    <TableCell>
                                      {record.dateOfAdmission
                                        ? new Date(record.dateOfAdmission).toLocaleDateString()
                                        : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                 ) : (
                    <Typography color="textSecondary">No academic history available.</Typography>
                 )}
            </TabPanel>

            {/* Tab 7: Status History */}
            <TabPanel value={tabValue} index={7}>
              {student.statusHistory && student.statusHistory.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Old Status</TableCell>
                        <TableCell>New Status</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Effective Date</TableCell>
                        <TableCell>Changed By</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {student.statusHistory.map((row: any) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.oldStatus}</TableCell>
                          <TableCell>{row.newStatus}</TableCell>
                          <TableCell>{row.reason || "-"}</TableCell>
                          <TableCell>
                            {row.effectiveDate
                              ? new Date(row.effectiveDate).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          <TableCell>{row.changedBy || "-"}</TableCell>
                          <TableCell>
                            {row.createdAt
                              ? new Date(row.createdAt).toLocaleDateString()
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">
                  No status history available.
                </Typography>
              )}
            </TabPanel>

            {/* Tab 8: Re-Study History */}
            <TabPanel value={tabValue} index={8}>
              {reStudyHistory && reStudyHistory.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Prev Session / Class</TableCell>
                        <TableCell>New Session / Class</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reStudyHistory.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {row.previousSession?.name} /{" "}
                            {row.previousClass?.name}
                          </TableCell>
                          <TableCell>
                            {row.newSession?.name} / {row.class?.name}
                          </TableCell>
                          <TableCell>{row.reason || "-"}</TableCell>
                          <TableCell>
                            {new Date(row.createdAt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">
                  No re-study history available.
                </Typography>
              )}
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default StudentProfile;
